/**
 * GlobalEnumDetailsDialog
 *
 * App-level mount of EnumValuesDetailsDialog driven by
 * `useEnumDetailsDialogStore`. Activated from grid header glyphs ("declared
 * enum" dropdown click) so the same review-and-refresh UX runs outside the
 * MetaInfo manager.
 */

import { useCallback, useMemo } from 'react';
import { EnumValuesDetailsDialog } from './EnumValuesDetailsDialog';
import { useEnumDetailsDialogStore } from './enumDetailsDialogStore';
import { useMetaInfo, useInvalidateMetaInfo } from '../../hooks/useMetaInfo';
import { removeEnumColumn } from '../../services/metaInfoApi';
import { openConfirmDialog } from '../confirm/confirmDialogStore';
import { useAnalytics } from '@/core/analytics/useAnalytics';

export function GlobalEnumDetailsDialog() {
  const { isOpen, context, closeDialog } = useEnumDetailsDialogStore();
  const invalidate = useInvalidateMetaInfo();
  const { track } = useAnalytics();
  const metaInfoQuery = useMetaInfo(
    context?.connectionId ?? null,
    context?.database ?? null,
  );

  // The store's context only carries the identity triple (+ declared source
  // for the refresh default) — the full declaration (needed for `lookup`)
  // lives in MetaInfo. Resolved once here so both `handleRemove` and the
  // `lookup` prop below share the same lookup.
  const decl = useMemo(() => {
    if (!context) return undefined;
    return metaInfoQuery.data?.enumColumns.find(
      (d) =>
        d.schema === context.schema &&
        d.table === context.table &&
        d.column === context.column &&
        (d.path ?? '') === (context.path ?? ''),
    );
  }, [context, metaInfoQuery.data]);

  const handleRemove = useCallback(() => {
    if (!context) return;
    const { connectionId, database, schema, table, column, source, path } = context;
    if (!decl) {
      closeDialog();
      return;
    }
    const label = `${schema}.${table}.${column}${path ? ` · ${path}` : ''}`;
    const isPath = !!path;
    openConfirmDialog({
      title: isPath
        ? 'Remove JSON-path enum declaration?'
        : 'Remove enum-column declaration?',
      description: isPath
        ? `Remove the JSON-path enum declaration for ${label}? Filters on this path will fall back to a free-text input.`
        : `Remove the enum-column declaration for ${label}? Filters on this column will fall back to a free-text input.`,
      confirmLabel: 'Remove',
      variant: 'destructive',
      onConfirm: async () => {
        await removeEnumColumn(connectionId, database, decl.id);
        await invalidate(connectionId, database);
        track('feature:enum_undeclare', { source, on_json_path: isPath });
        closeDialog();
      },
    });
  }, [context, decl, invalidate, track, closeDialog]);

  if (!isOpen || !context) return null;

  const cache = metaInfoQuery.data?.enumValueCache.find(
    (c) =>
      c.schema === context.schema &&
      c.table === context.table &&
      c.column === context.column &&
      (c.path ?? '') === (context.path ?? ''),
  );

  // An IMPLICIT lookup column (reference-table flag, no declaration of its
  // own) has no `decl.lookup` to read; the cache's own R1 `lookup` stamp is
  // the provenance, and it is what keeps "Refresh from data" working there.
  const lookupTarget = decl?.lookup ?? cache?.lookup;
  // …and the same two facts are what identify the implicit variant: no
  // declaration exists for this identity, yet a lookup target does. (A column
  // whose cache was never fetched has no stamp either — it falls back to the
  // plain dialog, which is also all it could act on.)
  const isImplicit = !decl && !!lookupTarget;

  return (
    <EnumValuesDetailsDialog
      open
      onOpenChange={(o) => {
        if (!o) closeDialog();
      }}
      schema={context.schema}
      table={context.table}
      column={context.column}
      cache={cache}
      declaredSource={context.declaredSource}
      connectionId={context.connectionId}
      database={context.database}
      engine={context.engine}
      onRemoveDeclaration={isImplicit ? undefined : handleRemove}
      path={context.path}
      pathKind={context.pathKind}
      lookup={lookupTarget}
      implicit={isImplicit}
      metadataEditors={context.metadataEditors}
      // Only a real declaration can carry a flag — an implicit column has
      // none, so `decl` being undefined already means "no badge".
      provenance={decl?.provenance}
    />
  );
}
